<template>
  <CollapsableSection title="SSH Settings">
    <p>
      Allow SSH Password Login and Enable Public Key Authentication cannot both be enabled if MFA
      is also enforced.
    </p>

    <Banner
      v-if="showBanner"
      bannerType="warning"
      :canCloseBanner="false"
      :class="$style.banner"
    >
      <template v-slot:content>
        <div
          :class="$style.bannerContent"
        >
          <div :class="$style.bannerTextContainer">
            <Icon
              :class="$style.bannerIcon"
              icon="info"
            />
            <span :class="$style.bannerText">
              SSH root login is managed by an SSH policy and can't be managed here.
            </span>
          </div>
          <Button
            :class="$style.bannerButton"
            hasOutline
            text="View SSH Policy"
            type="secondary"
            @click="handleBannerNavigation"
          />
        </div>
      </template>
    </Banner>

    <div
      v-for="(input, index) in inputs"
      :key="index"
      :class="[$style.inputRow, $style.rowSpacing]"
    >
      <div :class="$style.flexSingleWidth">
        <CheckboxWithLabel
          v-if="!input.showAsLabel"
          :label="input.label"
          :labelSubText="input.labelSubText"
          :name="input.field"
          :tooltip="input.tooltip"
          :value="input.value"
          @change="(checked) => handleCheckboxChange(input.field, checked)"
        />
        <p
          v-if="input.showAsLabel"
          :class="$style.aloneLabel"
        >
          {{ input.label }}
        </p>
      </div>
    </div>
  </CollapsableSection>
</template>
<script>
import { Banner, Button, Icon } from '@jumpcloud/ui-components';
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { mapGetters } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import CheckboxWithLabel from 'client/components/FormValidation/CheckboxWithLabel';
import CollapsableSection from 'components/ContentSection/CollapsableSection.vue';
import SystemInsightsConstants from 'client/util/Constants/SystemInsightsConstants';
import Util from 'client/util/Util';

const storeModel = StoreModelMap[ItemTypeConsts.system];

export default {
  name: 'SystemSettings',

  inject: ['$validator'],

  components: {
    CheckboxWithLabel,
    CollapsableSection,
    Banner,
    Button,
    Icon,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  props: {
    handleValueChange: {
      type: Function,
      required: true,
    },
  },

  computed: {
    ...mapGetters(storeModel, {
      isLinux: 'isLinux',
      isSystemInsightsSupportedBySystem: 'isSystemInsightsSupported',
    }),

    ...mapGetters('SettingsModule', {
      isSystemInsightsSupportedByOrg: 'hasSystemInsights',
    }),

    inputs() {
      const inputs = [];

      if (this.isLinux) {
        inputs.push({
          label: 'Allow SSH Password Login',
          field: 'allowSshPasswordAuthentication',
          tooltip: { text: 'When SSH Password Login is selected in conjunction with Public Key Authentication, users will be able to authenticate using either of the selected options. When just SSH Password Login is selected, the user will be forced to enter a valid password to authenticate.' },
          value: this.parent.allowSshPasswordAuthentication,
          showAsLabel: false,
        });

        inputs.push({
          label: 'Enable Public Key Authentication',
          field: 'allowPublicKeyAuthentication',
          tooltip: { text: 'When Public Key Authentication is selected in conjunction with SSH Password Login, users will be able to authenticate using either of the selected options. When just Public Key Authentication is selected, the user will be forced to have a properly configured public key to authenticate.' },
          value: this.parent.allowPublicKeyAuthentication,
          showAsLabel: false,
        });

        inputs.push({
          label: 'Allow SSH Root Login',
          field: 'allowSshRootLogin',
          value: this.parent.allowSshRootLogin,
          showAsLabel: this.parent.allowSshRootLoginDisable,
        });
      }

      return inputs;
    },

    showBanner() {
      return this.parent.allowSshRootLoginDisable;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options, true);
  },

  methods: {
    handleBannerNavigation() {
      const policyId = this.parent.allowSshRootLoginPolicyId;
      Util.redirect(`#/configurations/${policyId}/details`);
    },

    handleCheckboxChange(field, checked) {
      const isPasswordAuthCheckedAndPublicKeyAuthTrue = (field === 'allowSshPasswordAuthentication' && checked && this.parent.allowPublicKeyAuthentication);
      const isPublicKeyAuthCheckedAndPasswordAuthTrue = (field === 'allowPublicKeyAuthentication' && checked && this.parent.allowSshPasswordAuthentication);
      if (isPasswordAuthCheckedAndPublicKeyAuthTrue || isPublicKeyAuthCheckedAndPasswordAuthTrue) {
        this.handleValueChange(false, 'allowMultiFactorAuthentication');
      }

      if (field === 'systemInsights') {
        const { enabled, deferred } = SystemInsightsConstants.systemState;
        const state = checked ? enabled : deferred;
        this.handleValueChange({ state }, 'systemInsights');
      } else {
        this.handleValueChange(checked, field);
      }
    },
  },
};
</script>
<style module>
@import '../css/flex.css';

.rowSpacing {
  margin-bottom: 20px;
  margin-top: 0;
}

.aloneLabel {
  color: var(--jc-color-disabled-text);
  margin-left: var(--jc-spacer-medium);
}

.banner {
  margin-bottom: var(--jc-spacer-large);
  margin-top: var(--jc-spacer-large);
}

.bannerContent {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.bannerTextContainer {
  align-items: center;
  display: flex;
}

.bannerIcon {
  color: var(--jcWarning900);
}

.bannerText {
  margin-left: var(--jc-spacer-large);
  margin-right: var(--jc-spacer-large);
}

.bannerButton {
  margin-right: 2rem;
}

</style>
